<template>
  <v-col cols="12">
    <v-card class="elevation-4 mb-4">
      <!-- SELECT TIMEFRAME -->
      <v-row class="fill-height mb-3" align="center" justify="space-around">
        <v-btn-toggle v-model="selectedChartTimeframe" dark>
          <template v-for="timeframe in chartTimeframes">
            <v-btn :value="timeframe" :key="timeframe">{{ timeframe | toDays }}</v-btn>
          </template>
        </v-btn-toggle>
      </v-row>

      <!-- CHART -->
      <div :style="ui.chartMaxHeight">
        <canvas :id="chartName"></canvas>
      </div>

      <!-- TITLE OF Chart -->
      <v-card-title primary-title>
        <div>
          <h3 class="text-h5 mb0">Campaign Activity Stats</h3>
        </div>
      </v-card-title>
    </v-card>
  </v-col>
</template>

<script>
import Chart from 'chart.js'
import { mapState } from 'vuex'
import { StatsApi, EmailCampaignApi } from '@/services'
import * as R from 'ramda'
import { MAIL_EVENT_NAMES } from '@/constants'
import { CHART_COLORS } from '@/components/charts'

export default {
  name: 'campaign-activity-stats',
  props: {
    chartPrefix: {
      type: String,
      required: true
    },
    dealer: {
      type: Object,
      required: true
    },
    campaign: {
      type: Object,
      required: false
    }
  },
  data: function () {
    return {
      ui: {
        chartMaxHeight: 'max-height: 500px; min-height: 260px;'
      },
      chart: null,
      chartTimeframes: [1, 7, 30, 60, 90],
      selectedChartTimeframe: 7,
      displayCountFields: MAIL_EVENT_NAMES.map((field) => field + '_count'),
      campaignActivityStats: [], // data from api
      emailCampaigns: []
    }
  },
  computed: {
    ...mapState({
      accountKey: state =>
        R.path(['attributes', 'custom:account_key'], state.cognito.user)
    }),
    // dynamic so we can put multiple stats of this type on page
    chartName () {
      return this.chartPrefix + '-CampaignActivityStats'
    },
    xLabels () {
      return R.uniq(R.pluck('campaign_id', this.campaignActivityStats)).sort()
    },
    countFields () {
      return Object.keys((this.campaignActivityStats[0] || {}))
        .filter((field) => field.endsWith('_count'))
        .filter((field) => this.displayCountFields.includes(field))
        .sort()
    },
    datasets () {
      const countByCampaign = {}
      this.countFields.forEach((countKey) => {
        this.xLabels.forEach((campaignKey) => {
          countByCampaign[countKey] = countByCampaign[countKey] || []
          // could be optomized to groupby campaign_id
          let record = this.campaignActivityStats.find((statData) => statData.campaign_id === campaignKey)
          countByCampaign[countKey].push(record[countKey])
        })
      })
      return Object.entries(countByCampaign).map(([countKey, counts]) => {
        return {
          label: countKey.replace(/_count/, ''),
          data: counts
        }
      })
    },
    chartData () {
      return {
        type: 'bar',
        data: {
          labels: this.xLabels,
          datasets: this.datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            yAxes: [
              { ticks: { min: 0 } }
            ]
          },
          plugins: {
            colorschemes: {
              scheme: CHART_COLORS
            },
            datalabels: {
              display: false // this was the trick to turning off point labels - have to override plugin
            }
          },
          tooltips: {
            callbacks: {
              title: function (tooltipItems, data) {
                const title = this.lookupCampaignName(tooltipItems[0].xLabel)
                if (title) {
                  return `Campaign: ${title}`
                } else {
                  return `Id: ${tooltipItems[0].xLabel}`
                }
              }.bind(this)
            }
          }
        }
      }
    }
  },
  watch: {
    selectedChartTimeframe: {
      immediate: false,
      handler (newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          this.$logger.trace('Loading of daily stats...')
          this.refreshChart()
        } else {
          this.$logger.trace('Skipping refresh of daily stats...')
        }
      }
    },
    chartData: {
      immediate: false,
      handler (newVal, oldVal) {
        this.$logger.trace('Refreshing Activity By Dealer chart')
        this.chart.type = newVal.type
        this.chart.data = newVal.data
        // this.chart.data = this.mapChartDataLabels(newVal.data)
        this.chart.options = newVal.options
        this.chart.update()
      }
    },
    dealer: {
      immediate: false,
      handler (newDealer, oldDealer) {
        this.refreshEmailCampaigns()
        this.refreshChart()
      }
    },
    campaign: {
      immediate: false,
      handler (newCampaign, oldCampaign) {
        if (newCampaign && oldCampaign && newCampaign.id !== oldCampaign.id) {
          this.refreshChart()
        }
      }
    }
  },
  methods: {
    // mapChartDataLabels (chartData) {
    //   const chartDataCopy = JSON.parse(JSON.stringify(chartData))
    //   const campaignIdLabels = chartDataCopy.labels
    //   const nameLabels = campaignIdLabels.map((campaignId) => this.lookupCampaignName(campaignId) || campaignId)
    //   chartDataCopy.labels = nameLabels
    //   return chartDataCopy
    // },
    resetChartTimeframe () {
      this.selectedChartTimeframe = 7
    },
    lookupCampaignName (campaignId) {
      const campaign = this.emailCampaigns.find(campaign => campaignId.toString() === campaign.id.toString()) || {}
      return campaign.name
    },
    findOrCreateChart (chartId, chartData) {
      if (this.chart) {
        return this.chart
      } else {
        const ctx = document.getElementById(chartId)
        const chart = new Chart(ctx, {
          type: chartData.type,
          data: chartData.data,
          options: chartData.options
        })
        this.chart = chart
        return chart
      }
    },
    loadCampaignStats () {
      const params = {
        accountId: this.accountKey,
        senderId: this.dealer.dms_key,
        daysInPast: this.selectedChartTimeframe
      }

      if (this.campaign) {
        params.campaignId = this.campaign.id
      }

      StatsApi.fetchCampaignStatsBySender(params)
        .then(stats => {
          this.campaignActivityStats = stats
        })
        .catch(error => {
          this.$logger.error('Error loading Campaign Stats from API', error.message)
          this.$flash({
            showClose: true,
            message: `Error loading Campaign stats from API: ${error.message}`,
            type: 'mini',
            duration: 3000
          })
        })
    },
    refreshChart () {
      if (!this.selectedChartTimeframe) {
        this.resetChartTimeframe()
      }
      this.loadCampaignStats()
      this.findOrCreateChart(this.chartName, this.chartData)
    },
    refreshEmailCampaigns () {
      if (!this.dealer_id) return
      EmailCampaignApi.fetchEmailCampaignsAbbreviated(this.dealer_id)
        .then(resp => {
          this.emailCampaigns = resp.data
        })
        .catch(error => {
          this.$logger.error('Error refreshing campaigns', error.message)
          this.$flash({
            showClose: true,
            message: `Error Refreshing campaigns: ${error.message}`,
            type: 'error',
            duration: 0
          })
        })
    }
  },
  mounted () {
    this.$logger.trace('Loading CampaignActivityStats!!!')
    this.refreshEmailCampaigns()
    this.refreshChart()
  }
}
</script>

<style>
</style>
